import React, { useEffect, useState } from "react";
import RichTextEditor from "./RichTextEditor";
import aiDevelopmentService from "../../services/aiDevelopmentService";

export const EMPTY_STORY_FORM = {
  projectId: "",
  externalReference: "",
  title: "",
  description: "",
  businessContext: "",
  priority: "MEDIUM",
  acceptanceCriteria: "",
  requirePlanApproval: true,
  developerProviderOverride: "",
  qaProviderOverride: "",
  plannerProviderOverride: "",
};
function parseProviderOverrides(json) {
  try {
    return JSON.parse(json || "{}");
  } catch {
    return {};
  }
}
export const storyToForm = (story) => {
  const overrides = parseProviderOverrides(story.providerOverrides);
  return {
    ...EMPTY_STORY_FORM,
    projectId: story.projectId || "",
    externalReference: story.externalReference || "",
    title: story.title || "",
    description: story.description || "",
    businessContext: story.businessContext || "",
    priority: story.priority || "MEDIUM",
    acceptanceCriteria: (story.acceptanceCriteria || []).join("\n"),
    requirePlanApproval: Boolean(story.requirePlanApproval),
    developerProviderOverride: overrides.developer || "",
    qaProviderOverride: overrides.qa || "",
    plannerProviderOverride: overrides.planner || "",
    version: story.version,
  };
};
export const storyPayload = (form) => {
  const overrides = {};
  if (form.developerProviderOverride)
    overrides.developer = form.developerProviderOverride;
  if (form.qaProviderOverride) overrides.qa = form.qaProviderOverride;
  if (form.plannerProviderOverride)
    overrides.planner = form.plannerProviderOverride;
  const {
    developerProviderOverride,
    qaProviderOverride,
    plannerProviderOverride,
    ...rest
  } = form;
  return {
    ...rest,
    acceptanceCriteria: form.acceptanceCriteria
      .split("\n")
      .map((v) => v.trim())
      .filter(Boolean),
    providerOverrides: JSON.stringify(overrides),
  };
};

export default function AiStoryForm({
  form,
  setForm,
  projects,
  loading,
  onSubmit,
  onCancel,
  title,
  submitLabel,
  t,
  storyId,
}) {
  const onUploadImage = storyId
    ? (file) => aiDevelopmentService.attachStoryImage(storyId, file)
    : undefined;
  const [providers, setProviders] = useState([]);
  useEffect(() => {
    aiDevelopmentService
      .executionProviders()
      .then((list) => setProviders((list || []).filter((p) => p.enabled)))
      .catch(() => setProviders([]));
  }, []);
  return (
    <form className="ai-studio-panel ai-create-form" onSubmit={onSubmit}>
      <h2>{title}</h2>
      <div className="ai-form-grid">
        <label>
          {t("aiDevelopment.project")}
          <select
            required
            value={form.projectId}
            onChange={(e) => setForm({ ...form, projectId: e.target.value })}
          >
            <option value="">{t("common.select")}</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("aiDevelopment.reference")}
          <input
            value={form.externalReference}
            onChange={(e) =>
              setForm({ ...form, externalReference: e.target.value })
            }
          />
        </label>
        <label className="ai-span-2">
          {t("aiDevelopment.storyTitle")}
          <input
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </label>
        <div className="ai-span-2">
          <RichTextEditor
            required
            label={t("aiDevelopment.description")}
            value={form.description}
            onChange={(description) => setForm({ ...form, description })}
            onUploadImage={onUploadImage}
          />
        </div>
        <div className="ai-span-2">
          <RichTextEditor
            label={t("aiDevelopment.businessContext")}
            value={form.businessContext}
            onChange={(businessContext) =>
              setForm({ ...form, businessContext })
            }
            onUploadImage={onUploadImage}
          />
        </div>
        <label className="ai-span-2">
          {t("aiDevelopment.acceptance")}
          <textarea
            value={form.acceptanceCriteria}
            onChange={(e) =>
              setForm({ ...form, acceptanceCriteria: e.target.value })
            }
            placeholder={t("aiDevelopment.onePerLine")}
          />
        </label>
        <label>
          {t("aiDevelopment.priority")}
          <select
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
          >
            {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </label>
        <label className="ai-check">
          <input
            type="checkbox"
            checked={form.requirePlanApproval}
            onChange={(e) =>
              setForm({ ...form, requirePlanApproval: e.target.checked })
            }
          />
          {t("aiDevelopment.requireApproval")}
        </label>
        <label>
          {t("aiDevelopment.developerExecutionOverride")}
          <select
            value={form.developerProviderOverride}
            onChange={(e) =>
              setForm({ ...form, developerProviderOverride: e.target.value })
            }
          >
            <option value="">{t("aiDevelopment.inheritFromProject")}</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.id}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("aiDevelopment.qaExecutionOverride")}
          <select
            value={form.qaProviderOverride}
            onChange={(e) =>
              setForm({ ...form, qaProviderOverride: e.target.value })
            }
          >
            <option value="">{t("aiDevelopment.inheritFromProject")}</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.id}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("aiDevelopment.plannerExecutionOverride")}
          <select
            value={form.plannerProviderOverride}
            onChange={(e) =>
              setForm({ ...form, plannerProviderOverride: e.target.value })
            }
          >
            <option value="">{t("aiDevelopment.inheritFromProject")}</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.id}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="ai-actions">
        <button disabled={loading} className="btn btn-primary" type="submit">
          {submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="btn btn-default" onClick={onCancel}>
            {t("common.cancel")}
          </button>
        )}
      </div>
    </form>
  );
}
